import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TrainingCard from '@/components/feature/TrainingCard';
import { trainingEvents } from '@/mocks/home';
import { fetchActivityList } from '@/services/activity';
import { adaptTrainingEvents } from '@/services/adapters';
import { useRequest } from '@/hooks/useRequest';

const HERO_IMAGE =
  'https://readdy.ai/api/search-image?query=Wide%20cinematic%20view%20of%20a%20grand%20Chinese%20medicine%20academic%20conference%20hall%20with%20warm%20deep%20red%20lighting%2C%20silhouetted%20audience%2C%20elegant%20stage%20with%20soft%20atmospheric%20haze%2C%20stylized%20editorial%20photography%2C%20dark%20moody%20tones%20for%20text%20overlay%20contrast&width=1920&height=560&seq=act-page-hero&orientation=landscape';

const TYPES = ['全部', '研修班', '精研班', '培训班', '培训', '研讨会'];

export default function ActivitiesPage() {
  const [year, setYear] = useState<'全部' | '2026' | '2027'>('全部');
  const [type, setType] = useState('全部');
  const [onlyOpen, setOnlyOpen] = useState(false);

  // 接口失败时降级为 mock 数据
  const { data: events } = useRequest(
    () => fetchActivityList({ pageIndex: 1, pageSize: 999 }).then((res) => adaptTrainingEvents(res.list)),
    { fallback: trainingEvents },
  );

  const filtered = useMemo(() => {
    return events.filter((item) => {
      const yearOk = year === '全部' || String(item.year) === year;
      const typeOk = type === '全部' || item.type === type;
      const openOk = !onlyOpen || item.enrolled < item.capacity;
      return yearOk && typeOk && openOk;
    });
  }, [events, year, type, onlyOpen]);

  const totalCapacity = events.reduce((sum, item) => sum + item.capacity, 0);

  const chipClass = (active: boolean) =>
    `px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
      active
        ? 'bg-primary-500 text-background-50'
        : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800'
    }`;

  return (
    <div className="w-full min-h-screen bg-background-50 font-body">
      <Navbar />

      <main>
        {/* Hero header */}
        <header className="relative w-full pt-32 pb-14 md:pt-40 md:pb-20 overflow-hidden bg-primary-900">
          <img
            src={HERO_IMAGE}
            alt="会议培训"
            title="元合 VITA 会议培训中心"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/70 to-primary-950/85"></div>
          <div className="relative w-full px-4 md:px-8">
            <nav className="flex items-center gap-2 text-sm text-background-100/80 mb-5">
              <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
                首页
              </Link>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-background-50">会议培训</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight text-balance">
              会议培训中心
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-background-100/90 leading-relaxed">
              汇聚国医大师、名院专家与前沿技术，以专题研修班、国医大师精研班、适宜技术培训班等形式，
              持续为基层医疗机构培养中西医结合骨干力量。
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-background-50/10 border border-background-50/25 text-background-50 text-sm">
                <span className="w-4 h-4 flex items-center justify-center text-secondary-300">
                  <i className="ri-calendar-event-line"></i>
                </span>
                全年 {events.length} 场培训
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-background-50/10 border border-background-50/25 text-background-50 text-sm">
                <span className="w-4 h-4 flex items-center justify-center text-secondary-300">
                  <i className="ri-group-line"></i>
                </span>
                预计招收 {totalCapacity} 人
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-background-50/10 border border-background-50/25 text-background-50 text-sm">
                <span className="w-4 h-4 flex items-center justify-center text-secondary-300">
                  <i className="ri-award-line"></i>
                </span>
                国医大师亲临授课
              </span>
            </div>
          </div>
        </header>

        {/* Filters */}
        <section className="w-full border-b border-background-200 bg-background-50 sticky top-0 z-30">
          <div className="w-full px-4 md:px-8 py-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500 mr-1 whitespace-nowrap">年份</span>
              {(['全部', '2026', '2027'] as const).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setYear(item)}
                  className={chipClass(year === item)}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500 mr-1 whitespace-nowrap">类型</span>
              {TYPES.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setType(item)}
                  className={chipClass(type === item)}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 lg:ml-auto">
              <button
                type="button"
                role="switch"
                aria-checked={onlyOpen}
                onClick={() => setOnlyOpen((prev) => !prev)}
                className="inline-flex items-center gap-2 cursor-pointer whitespace-nowrap group"
              >
                <span
                  className={`relative inline-flex w-9 h-5 shrink-0 items-center rounded-full transition-colors ${
                    onlyOpen ? 'bg-primary-500' : 'bg-background-300 group-hover:bg-background-400'
                  }`}
                >
                  <span
                    className={`absolute w-4 h-4 rounded-full bg-background-50 transition-all ${
                      onlyOpen ? 'left-[18px]' : 'left-0.5'
                    }`}
                  ></span>
                </span>
                <span className={`text-sm transition-colors ${onlyOpen ? 'text-primary-600 font-medium' : 'text-foreground-500'}`}>
                  只看可报名场次
                </span>
              </button>
              <span className="w-px h-4 bg-background-300"></span>
              <span className="text-sm text-foreground-500 whitespace-nowrap">
                共 <span className="font-semibold text-primary-600">{filtered.length}</span> 场
              </span>
            </div>
          </div>
        </section>

        {/* List */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-8">
            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
                {filtered.map((item) => (
                  <TrainingCard key={item.id} item={item} />
                ))}
              </div>
            ) : (
              <div className="py-20 text-center">
                <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </span>
                <p className="text-foreground-500">暂无符合条件的培训，请调整筛选条件</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-14 md:py-16 bg-secondary-100">
          <div className="w-full px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                想为您的机构定制专场培训？
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                我们可围绕重点病种、学科建设与适宜技术，提供一站式定制化培训解决方案。
              </p>
            </div>
            <Link
              to="/"
              state={{ scrollTo: 'contact' }}
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-customer-service-2-line"></i>
              </span>
              联系定制培训
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}